import {
  MetricCapability,
  MetricInputKey,
  MetricRole,
  MetricSolution,
} from './metric-taxonomy.js';

/** How a metric's number should be read and formatted. */
export enum MetricValueKind {
  /** A money amount. Only these can roll into the ROI hero. */
  Currency = 'currency',
  /** 0–100, rendered with one decimal and a % sign. */
  Percentage = 'percentage',
  /** A plain count (scans, countries, certificates). */
  Count = 'count',
  /** Elapsed time, rendered in days. */
  Duration = 'duration',
}

/**
 * One analytics metric, tagged for every surface that consumes the catalogue:
 * the platform's per-role analytics tabs, the marketing site's solution pages,
 * and the marketing site's role pages.
 *
 * This is the single definition. Nothing downstream may retype a title, a unit or
 * a description — the same argument as the glossary (working-conventions §44):
 * copies drift silently because nothing compares them.
 */
export interface IAnalyticsMetric {
  /** Stable id. Never reuse or repurpose one — the website links to these. */
  id: string;

  /**
   * Card title. Keep it short enough to sit on one line in a card header; the
   * long form belongs in `description`.
   */
  title: string;

  /** The basis line under the value, e.g. "total scans", "countries". */
  unit: string;

  /** One sentence, customer-facing. Shown in the card's info tooltip. */
  description: string;

  // ---- the four taxonomy fields -------------------------------------------

  /** Solution areas this metric provides evidence for. */
  solutions: MetricSolution[];

  /** Company roles whose analytics tab should show it. */
  roles: MetricRole[];

  /** Capabilities this metric proves worked. */
  evidences: MetricCapability[];

  /**
   * Present ONLY when the metric cannot be computed because its source events are
   * not captured anywhere. Absent means computable — modulo `inputs`.
   *
   * This is the one availability fact that does not derive from anything else.
   * "Needs an assumption" and "the data does not exist" are independent problems:
   * supplying a rate does not help if there is nothing to apply it to. Read the
   * A/B/C summary via `metricTier()`; never store it.
   *
   * `reason` is customer-facing — a blocked card renders no figure, so this text
   * is the entire explanation the reader gets.
   */
  blocked?: { reason: string };

  /**
   * Brand-supplied assumptions required before this metric can render a value.
   * Empty means it computes from recorded facts alone. A card whose inputs are
   * unset renders a click-to-set empty state, never a silent default.
   */
  inputs: MetricInputKey[];

  // ---- rendering -----------------------------------------------------------

  valueKind: MetricValueKind;

  /**
   * Whether this metric's value sums into the single ROI hero.
   *
   * TRUE only for currency metrics representing value delivered. Rates and counts
   * are false — they describe the mechanism, not the return.
   *
   * The hero is the ONLY place a multiple is shown, deliberately. Dividing each
   * card by the same denominator (total Vera spend) would produce per-card
   * multiples that are not additive, and a reader confronted with six of them will
   * add them. One numerator sum, one division, one multiple.
   */
  contributesToRoi: boolean;

  /**
   * Plain-language statement of the computation, shown beside the value so the
   * number is auditable. For tier B this must name the assumption it rests on.
   */
  formula: string;
}
